import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface EmptyStateProps {
  /** Defaults to an inbox. Use something domain-specific where you can. */
  icon?: LucideIcon;
  title: ReactNode;
  message?: ReactNode;
  /** Primary call to action — usually a `<Button>`. */
  action?: ReactNode;
  /** Secondary link or button next to the action. */
  secondaryAction?: ReactNode;
  /** Adds the kerb texture behind the icon. Reserve it for full-page states. */
  kerb?: boolean;
  /** Heading level for the title. Use `h1` when this IS the page. */
  titleAs?: 'h1' | 'h2' | 'h3';
  size?: 'sm' | 'md';
  className?: string;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  message,
  action,
  secondaryAction,
  kerb = false,
  titleAs: Heading = 'h3',
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        size === 'md' ? 'px-gutter py-12' : 'px-4 py-8',
        className,
      )}
    >
      <div className="relative flex size-14 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface-2">
        {kerb ? <span aria-hidden="true" className="kerb absolute inset-0" /> : null}
        <Icon aria-hidden="true" className="relative size-6 text-text-faint" />
      </div>

      <div className="max-w-prose">
        <Heading className={cn(Heading === 'h1' ? 'text-2xl' : 'text-lg', 'text-text')}>{title}</Heading>
        {message ? <p className="mt-1 text-base text-text-muted">{message}</p> : null}
      </div>

      {action || secondaryAction ? (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
